import {
    BottomNavigation,
    Box,
    Paper,
    SvgIcon,
    useMediaQuery,
    Popover,
    Button,
    Stack
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

import {
    aiChatDataRoute,
    dashboardBaseRoute, dashboardListRoute,
    dashboardNavForMobileRoute, noDashboardForMobileRoute,
    productDataRoute,
    staffDataRoute,
    turnOverDataRoute
} from '../routes/dashboard';

import { usePathname } from "../hooks/use-pathname";
import { useCssVars } from "./dashboard/mobile-nav";
import AIIcon from "../icons/untitled-ui/duocolor/aiIcon";
import localStrings from "../localStrings";
import { ShoppingBag03 } from "@untitled-ui/icons-react";
import Users03 from "../icons/untitled-ui/duocolor/users-03";
import CurrencyBitcoinCircle from "../icons/untitled-ui/duocolor/currency-bitcoin-circle";
import React, {useEffect, useState} from "react";
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import DateSelectorDialogMobile from "../components/DateSelectorDialogMobile";
import DashBoardIcon from "../icons/untitled-ui/duocolor/dashboard";
import HomeSmile from "../icons/untitled-ui/duocolor/home-smile";
import {executeQueryUtil} from "../utils/gqlUtil";
import {getDashBoardsQuery} from "../gql/dashBoardGql";
import useAuth from "../hooks/useAuth";

export const TOP_NAV_HEIGHT_MOBILE = 56;

export function getMobileBottomBarHeight(lgUp) {
    return lgUp ? 0 : TOP_NAV_HEIGHT_MOBILE;
}

function getChildrenDashBoards(dashBoards) {
    let map = (dashBoards || []).map((dashboard) => {
        return {
            icon: <DashBoardIcon/>,
            label: dashboard.name,
            to: `/${dashboardBaseRoute}/customBoards/${dashboard.id}`
        }
    });
    if (dashBoards && dashBoards.length > 0) {
        map.push({
            icon: <DashBoardIcon/>,
            label: localStrings.manageMyBoards,
            to: `/${dashboardBaseRoute}/${dashboardListRoute}`
        });
    }

    return map;
}

export default function MobileBottomBar() {
    const [activePopoverItem, setActivePopoverItem] = useState(null);

    const {
        userInDb,
        navbarMobileReload
    } = useAuth();

    const theme = useTheme();
    const lgUp = useMediaQuery((theme) => theme.breakpoints.up("lg"));
    const pathname = usePathname();
    const navigate = useNavigate();
    const cssVars = useCssVars('evident');

    const [openDatePicker, setOpenDatePicker] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [dashBoards, setDashBoards] = useState(null);

    const handleOpenPopover = (event, item) => {
        setAnchorEl(event.currentTarget);
        setActivePopoverItem(item);
    };

    useEffect(() => {
        if (!userInDb) {
            return;
        }
        async function setItemSourceAsync() {
            let result = await executeQueryUtil(getDashBoardsQuery(userInDb.id));
            setDashBoards(result?.data?.getDashBoards || []);
        }
        setItemSourceAsync();
    }, [userInDb, navbarMobileReload]);


    const handleClosePopover = () => {
        setAnchorEl(null);
    };

    const popoverOpen = Boolean(anchorEl);

    const navItems = [
        {
            icon:  <HomeSmile/>,
            label: localStrings.mobileBottomBar.homeLabel,
            to: `/${dashboardBaseRoute}`,
            isActive: pathname === (`/${dashboardBaseRoute}`)
        },

        {
            icon: <ShoppingBag03 />,
            label: 'Données',
            hasChildren: true,
            onClick: (e) => handleOpenPopover(e, navItems[1]),
            children: [
                {
                    icon: <ShoppingBag03 />,
                    label: localStrings.mobileBottomBar.productLabel,
                    to: `/${dashboardBaseRoute}/${productDataRoute}`
                },
                {
                    icon: <Users03 />,
                    label: localStrings.mobileBottomBar.staffLabel,
                    to: `/${dashboardBaseRoute}/${staffDataRoute}`
                },
                {
                    icon: <CurrencyBitcoinCircle />,
                    label: localStrings.mobileBottomBar.turnOverLabel,
                    to: `/${dashboardBaseRoute}/${turnOverDataRoute}`
                }
            ],
            isActive: [
                `/${dashboardBaseRoute}/${productDataRoute}`,
                `/${dashboardBaseRoute}/${staffDataRoute}`,
                `/${dashboardBaseRoute}/${turnOverDataRoute}`
            ].some((route) => pathname.startsWith(route))
        },
        {
            icon: <DashBoardIcon />,
            label: localStrings.mobileBottomBar.dashboardNavForMobile,
            to: dashBoards && dashBoards.length === 0 ? `/${dashboardBaseRoute}/${noDashboardForMobileRoute}`: null,
            // isActive: pathname.startsWith(`/${dashboardBaseRoute}/${dashboardNavForMobileRoute}`),
            onClick: (e) => handleOpenPopover(e, navItems[2]),
            children:
                getChildrenDashBoards(dashBoards),
            isActive: (dashBoards || []).some((dashboard) =>
                pathname.startsWith(`/${dashboardBaseRoute}/customBoards/${dashboard.id}`)
            )

        },
        {
            icon: <AIIcon />,
            label: localStrings.mobileBottomBar.aiLabel,
            to: `/${dashboardBaseRoute}/${aiChatDataRoute}`,
            isActive: pathname.startsWith(`/${dashboardBaseRoute}/${aiChatDataRoute}`)
        },
        // {
        //     icon: <CalendarTodayIcon />,
        //     label: localStrings.mobileBottomBar.periodLabel,
        //     onClick: () => setOpenDatePicker(true)
        // }
    ];

    if (lgUp) return null;

    return (
        <>
            <DateSelectorDialogMobile
                openDatePicker={openDatePicker}
                setOpenDatePicker={setOpenDatePicker}
            />

            <Paper
                sx={{
                    position: 'fixed',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    zIndex: theme.zIndex.appBar + 1,
                    backdropFilter: 'blur(10px)',
                    backgroundColor: alpha(theme.palette.background.paper, 0.8),
                    borderTop: `1px solid ${theme.palette.divider}`,
                    ...cssVars
                }}
                elevation={3}
            >
                <BottomNavigation sx={{ display: 'flex', justifyContent: 'space-evenly' }}>
                    {navItems.map((item, index) => {
                        const isActive = item.isActive;

                        const boxProps = item.to
                            ? { component: RouterLink, to: item.to }
                            : { onClick: item.onClick };

                        return (
                            <Box
                                key={index}
                                {...boxProps}
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flex: 1,
                                    py: 1,
                                    color: isActive
                                        ? 'var(--nav-item-icon-active-color)'
                                        : 'var(--nav-item-icon-color)',
                                    textDecoration: 'none',
                                }}
                            >
                                <SvgIcon fontSize="small">{item.icon}</SvgIcon>
                                <Box
                                    component="span"
                                    sx={{
                                        fontSize: '0.75rem',
                                        mt: '4px',
                                        color: 'inherit'
                                    }}
                                >
                                    {item.label}
                                </Box>
                            </Box>
                        );
                    })}
                </BottomNavigation>
            </Paper>

            {/* Popover pour les sous-éléments */}
            <Popover
                open={popoverOpen}
                anchorEl={anchorEl}
                onClose={handleClosePopover}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                PaperProps={{
                    sx: {
                        p: 1,
                        borderRadius: 0,
                        width: '100vw', // largeur totale de l’écran
                        maxWidth: '100vw',
                        left: '0 !important', // force le positionnement au bord gauche
                        backgroundColor: theme.palette.background.paper,
                        borderTop: `1px solid ${theme.palette.divider}`,
                        boxShadow: theme.shadows[3],
                    },
                }}
                sx={{
                    left: 0, // au cas où MUI Popover interprète une autre position
                }}
            >
                <Stack spacing={1} px={2} py={1}>
                    {activePopoverItem?.children?.map((child, i) => (
                        <Button
                            key={i}
                            startIcon={child.icon}
                            fullWidth
                            variant="outlined"
                            onClick={() => {
                                if (child.to) {
                                    navigate(child.to);
                                }
                                // navigate(child.to);
                                handleClosePopover();
                            }}
                        >
                            {child.label}
                        </Button>
                    ))}
                </Stack>
            </Popover>



        </>
    );
}
